import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
    catchError,
    concatMap,
    flatMap,
    map,
    mergeMap,
    switchMap,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import { AttachmentService } from '../../attachment-view-feature/attachment.service';
import * as fromShared from '../../shared';
import { LoggingService } from '../../shared/services/logging/logging.service';
import * as fromUser from '../../user-info/store/user.reducer';
import { TimeCardService } from '../time-card.service';
import * as fromTimecardDoc from './time-card-documentation.actions';
import { TimeCardDocumentation } from './time-card-documentation.model';
import * as fromDocReducer from './time-card-documentation.reducer';
import * as fromTimecard from './time-card.selectors';

@Injectable()
export class TimeCardDocumentationEffects {
    constructor(
        private actions$: Actions,
        private service: TimeCardService,
        private log: LoggingService,
        private attachmentService: AttachmentService,
        private store: Store
    ) {}

    getTimecardDocumentation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromTimecardDoc.getTimecardDocumentations),
            tap(a =>
                this.log.verbose({
                    type: 'TimecardDocumentationEffects:getTimecardDocumentation$',
                    a
                })
            ),
            switchMap(a =>
                this.service.getTimecardDocumentation(a.id, a.processId).pipe(
                    concatMap(tcs => [
                        fromTimecardDoc.clearTimeCardDocumentations(),
                        fromTimecardDoc.loadTimeCardDocumentations({
                            timeCardDocumentations: tcs
                        })
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    putTimecardDocumentation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromTimecardDoc.putTimecardDocumentation),
            switchMap(a =>
                this.service.putTimecardDocumentation(a.doc).pipe(
                    concatMap(resp => {
                        const savedDocs = a.doc ?? [];
                        return [
                        fromTimecardDoc.deleteTimeCardDocumentations({
                            ids: resp.Items.map(x => `${x.EmpTimeID}--1`)
                        }),
                        fromTimecardDoc.upsertTimeCardDocumentations({
                            timeCardDocumentations: resp.Items
                        }),
                        ...savedDocs.map(doc =>
                            fromTimecardDoc.updateTimecardDocumentationSuccess({
                                doc
                            })
                        ),
                        new fromShared.Errors.ClearErrors(),
                        new fromShared.Errors.SetErrors(
                            fromShared.Errors.MapErrorsToStatus(resp)
                        )
                    ];
                    }),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });

    removeTimecardDocumentation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromTimecardDoc.removeTimecardDocumentation),
            switchMap(a =>
                this.service.deleteTimecardDocumentation(a.ids).pipe(
                    withLatestFrom(
                        this.store.pipe(select(fromTimecard.selectAll)),
                        this.store.pipe(select(fromUser.getUserInfo)),
                        this.store.pipe(select(fromShared.selectCurrent))
                    ),
                    concatMap(([resp, tcs, user, process]) =>
                        fromShared.Errors.IsSuccessStatus(resp.StatusCode)
                            ? [
                                  fromTimecardDoc.getTimecardDocumentations({
                                      id: tcs[0].EmployeeListID,
                                      processId: process.ProcessID
                                  }),
                                  fromTimecardDoc.updateTimecardDocumentationSuccess(
                                      { doc: { id: a.ids[0] } }
                                  )
                              ]
                            : [
                                  new fromShared.Errors.SetErrors(
                                      fromShared.Errors.MapErrorsToStatus(resp)
                                  )
                              ]
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });

    AddTimecardDocumentationAttachment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromTimecardDoc.addTimecardDocumentationAttachment),
            tap(action =>
                this.log.verbose({
                    type: 'TimecardDocumentationEffects:AddTimecardDocumentationAttachment',
                    action
                })
            ),
            switchMap(action =>
                this.attachmentService
                    .saveAttachment(
                        action.process,
                        action.entityId,
                        action.fileId,
                        action.meta,
                        action.file
                    )
                    .pipe(
                        tap(resp =>
                            this.log.debug({
                                type: 'TimecardDocumentationEffects:AddTimecardDocumentationAttachment',
                                resp
                            })
                        ),
                        flatMap(resp => {
                            return fromShared.Errors.IsSuccessStatus(
                                resp.StatusCode
                            )
                                ? [
                                      fromTimecardDoc.addTimecardDocumentationAttachmentSuccess(
                                          { res: resp }
                                      )
                                  ]
                                : [
                                      new fromShared.Errors.SetErrors(
                                          fromShared.Errors.MapErrorsToStatus(
                                              resp
                                          )
                                      )
                                  ];
                        }),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        );
    });
    addTimecardDocumentationAttachmentSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromTimecardDoc.addTimecardDocumentationAttachmentSuccess),
            withLatestFrom(this.store.pipe(select(fromDocReducer.selectAll))),
            map(([a, docs]) =>
                fromTimecardDoc.upsertTimeCardDocumentation({
                    timeCardDocumentation: {
                        id: `${
                            docs.find(
                                doc => doc.WorkID === a.res.Items[0].Expense_id
                            )?.EmpTimeID
                        }-${a.res.Items[0].Expense_id}`,
                        WorkID: a.res.Items[0].Expense_id ?? 0,
                        File_unique_id: a.res.Items[0].Unique_Id
                    }
                })
            )
        );
    });
    removeTimecardDocumentationAttachment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromTimecardDoc.removeTimecardDocumentationAttachment),
            switchMap(a =>
                this.attachmentService.deleteAttachment(a.fileId).pipe(
                    withLatestFrom(
                        this.store.pipe(select(fromDocReducer.selectAll))
                    ),
                    mergeMap(([resp, docs]) =>
                        fromShared.Errors.IsSuccessStatus(resp.StatusCode)
                            ? [
                                  fromTimecardDoc.removeTimecardDocumentationAttachmentSuccess(
                                      {
                                          fileId: resp.Items[0]
                                              .File_unique_id as string
                                      }
                                  ),
                                  fromTimecardDoc.upsertTimeCardDocumentation({
                                      timeCardDocumentation: {
                                          ...(docs.find(
                                              d => d.File_unique_id === a.fileId
                                          ) as TimeCardDocumentation),
                                          File_unique_id: undefined
                                      }
                                  })
                              ]
                            : [
                                  new fromShared.Errors.SetErrors(
                                      fromShared.Errors.MapErrorsToStatus(resp)
                                  )
                              ]
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
}
